import { useState } from "react";
import type { FieldSession } from "@/pages/fieldtracking/page";

interface LiveEmployee {
  empId: string;
  name: string;
  avatar: string;
  jobNo: string;
  location: string;
  lastUpdate: string;
  status: string;
}

interface GPSMapViewProps {
  activeSessions: FieldSession[];
  allSessions: FieldSession[];
  onSelectSession: (session: FieldSession) => void;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function getElapsed(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const SURVEYOR_COLORS = ["#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#3b82f6", "#ec4899"];

export default function GPSMapView({ activeSessions, allSessions, onSelectSession }: GPSMapViewProps) {
  const [selectedEmp, setSelectedEmp] = useState<string | null>(null);
  const [showTrail, setShowTrail] = useState(true);

  // Build active employees from active sessions
  const activeEmps: LiveEmployee[] = activeSessions.map((s) => ({
    empId: s.empId,
    name: s.empName,
    avatar: s.empAvatar,
    jobNo: s.jobNo,
    location: s.checkInAddress,
    lastUpdate: "just now",
    status: "Active",
  }));
  const selectedEmpData = selectedEmp ? activeEmps.find((e) => e.empId === selectedEmp) : null;
  const selectedSession = selectedEmp ? activeSessions.find((s) => s.empId === selectedEmp) : null;

  // Build map embed URL centered on Hyderabad
  const mapSrc = "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d242000!2d78.4294!3d17.3850!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1712345678901!5m2!1sen!2sin";

  return (
    <div className="grid grid-cols-3 gap-6">
      {/* Map Panel */}
      <div className="col-span-2 bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-semibold text-sm text-gray-800">Live GPS Map</span>
            <span className="flex items-center gap-1.5 text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
              {activeEmps.length} Active
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTrail((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${showTrail ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-gray-100 text-gray-500 border border-gray-200"}`}
            >
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-route-line" /></span>
              Trail
            </button>
            <button
              onClick={() => setSelectedEmp(null)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-focus-3-line" /></span>
              Reset View
            </button>
          </div>
        </div>

        {/* Map */}
        <div className="relative">
          <iframe
            title="GPS Field Map"
            src={mapSrc}
            className="w-full h-96"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
          />

          {/* Overlay: Surveyor Pins */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Simulated pin overlays positioned over the map */}
            {activeEmps.map((emp, idx) => {
              const color = SURVEYOR_COLORS[idx % SURVEYOR_COLORS.length];
              const isSelected = selectedEmp === emp.empId;
              // Approximate pixel positions for demo (Hyderabad area)
              const positions = [
                { top: "55%", left: "42%" },
                { top: "38%", left: "35%" },
                { top: "28%", left: "52%" },
              ];
              const pos = positions[idx] || { top: "50%", left: "50%" };
              return (
                <div
                  key={emp.empId}
                  className="absolute pointer-events-auto"
                  style={{ top: pos.top, left: pos.left, transform: "translate(-50%, -100%)" }}
                >
                  {/* Pulse ring */}
                  {isSelected && (
                    <div
                      className="absolute inset-0 rounded-full animate-ping opacity-40"
                      style={{ background: color, width: 40, height: 40, top: -4, left: -4 }}
                    />
                  )}
                  {/* Pin */}
                  <button
                    onClick={() => setSelectedEmp(selectedEmp === emp.empId ? null : emp.empId)}
                    className="relative flex flex-col items-center cursor-pointer"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white"
                      style={{ background: color, boxShadow: isSelected ? `0 0 0 3px ${color}40` : "0 2px 8px rgba(0,0,0,0.3)" }}
                    >
                      {emp.avatar}
                    </div>
                    {/* Tooltip */}
                    {isSelected && (
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white rounded-xl border border-gray-200 p-2.5 w-44 text-left z-10" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}>
                        <p className="text-xs font-bold text-gray-900">{emp.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{emp.jobNo}</p>
                        <div className="flex items-center gap-1 mt-1.5">
                          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-map-pin-line text-xs" style={{ color }} /></span>
                          <span className="text-xs text-gray-600">{emp.location}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">Updated {emp.lastUpdate}</p>
                      </div>
                    )}
                    {/* Pin tail */}
                    <div className="w-0 h-0" style={{ borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: `8px solid ${color}` }} />
                  </button>
                </div>
              );
            })}

            {/* Trail lines (simulated) — using viewBox coords, NOT percentages */}
            {showTrail && (
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ pointerEvents: "none" }}
              >
                <defs>
                  <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="3" refY="2" orient="auto">
                    <polygon points="0 0, 6 2, 0 4" fill="#10b981" opacity="0.6" />
                  </marker>
                </defs>
                {/* Ravi Teja trail */}
                <polyline
                  points="38,62 40,59 42,55"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="0.6"
                  strokeDasharray="1.5,1"
                  opacity="0.6"
                  markerEnd="url(#arrowhead)"
                />
                {/* Naresh trail */}
                <polyline
                  points="31,44 33,41 35,38"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="0.6"
                  strokeDasharray="1.5,1"
                  opacity="0.6"
                />
                {/* Mahesh trail */}
                <polyline
                  points="49,32 51,30 52,28"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="0.6"
                  strokeDasharray="1.5,1"
                  opacity="0.6"
                />
              </svg>
            )}
          </div>

          {/* Legend */}
          <div className="absolute bottom-3 left-3 bg-white/95 rounded-xl border border-gray-200 p-2.5" style={{ backdropFilter: "blur(8px)" }}>
            <p className="text-xs font-semibold text-gray-700 mb-1.5">Active Surveyors</p>
            <div className="space-y-1">
              {activeEmps.map((emp, idx) => (
                <div key={emp.empId} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: SURVEYOR_COLORS[idx % SURVEYOR_COLORS.length] }} />
                  <span className="text-xs text-gray-600">{emp.name}</span>
                  <span className="text-xs text-gray-400">· {emp.location}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Timestamp Strip */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <div className="grid grid-cols-3 gap-3">
            {activeEmps.map((emp, idx) => {
              const session = activeSessions.find((s) => s.empId === emp.empId);
              const color = SURVEYOR_COLORS[idx % SURVEYOR_COLORS.length];
              return (
                <button
                  key={emp.empId}
                  onClick={() => {
                    setSelectedEmp(emp.empId);
                    if (session) onSelectSession(session);
                  }}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-gray-100 hover:border-gray-200 cursor-pointer transition-all text-left"
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: color }}>
                    {emp.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{emp.name}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="w-3 h-3 flex items-center justify-center"><i className="ri-login-circle-line text-xs text-amber-500" /></span>
                      <span className="text-xs text-gray-500">
                        {session ? formatTime(session.checkInTime) : "—"}
                      </span>
                      {session && (
                        <span className="text-xs text-amber-600 font-medium ml-1">
                          ({getElapsed(session.checkInTime)})
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="space-y-4">
        {/* Selected Surveyor Detail */}
        {selectedEmpData && selectedSession ? (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-semibold text-sm text-gray-800">Surveyor Detail</span>
              <button onClick={() => setSelectedEmp(null)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-sm" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold">
                  {selectedEmpData.avatar}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{selectedEmpData.name}</p>
                  <p className="text-xs text-gray-500">{selectedEmpData.empId}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium mt-0.5">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                    Active in Field
                  </span>
                </div>
              </div>

              <div className="space-y-2.5">
                <div className="flex items-start gap-2.5 p-3 bg-amber-50 rounded-xl border border-amber-100">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-login-circle-line text-amber-600 text-sm" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-amber-700">Checked In</p>
                    <p className="text-sm font-bold text-gray-900">{formatTime(selectedSession.checkInTime)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{selectedSession.checkInAddress}</p>
                    <p className="text-xs text-gray-400">{selectedSession.checkInLat.toFixed(5)}, {selectedSession.checkInLng.toFixed(5)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 p-3 bg-amber-50 rounded-xl border border-amber-100">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-time-line text-amber-600 text-sm" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-amber-700">Time in Field</p>
                    <p className="text-sm font-bold text-gray-900">{getElapsed(selectedSession.checkInTime)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Still active — no checkout yet</p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-file-list-3-line text-gray-500 text-sm" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-gray-600">Job Card</p>
                    <p className="text-sm font-bold text-gray-900">{selectedSession.jobNo}</p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{selectedSession.jobTitle}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500 pt-1">
                <span className="flex items-center gap-1">
                  <i className="ri-image-line" />
                  {selectedSession.photos.length} photos uploaded
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-map-pin-line text-amber-500" />
                  {selectedEmpData.location}
                </span>
              </div>

              <button
                onClick={() => onSelectSession(selectedSession)}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line" /></span>
                View Full Session
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-semibold text-sm text-gray-800 mb-3">Active Sessions</h3>
            <div className="space-y-2.5">
              {activeSessions.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">No active sessions right now</p>
              )}
              {activeSessions.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => { setSelectedEmp(s.empId); onSelectSession(s); }}
                  className="w-full flex items-start gap-3 p-3 rounded-xl bg-gray-50 hover:bg-amber-50 cursor-pointer transition-colors border border-transparent hover:border-amber-100 text-left"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ background: SURVEYOR_COLORS[idx % SURVEYOR_COLORS.length] }}
                  >
                    {s.empAvatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{s.empName}</p>
                    <p className="text-xs text-gray-500 truncate">{s.jobNo}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-amber-600">In: {formatTime(s.checkInTime)}</span>
                      <span className="text-xs text-gray-400">({getElapsed(s.checkInTime)})</span>
                    </div>
                  </div>
                  <span className="w-2 h-2 bg-amber-500 rounded-full mt-1 flex-shrink-0 animate-pulse" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Today's Summary */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="font-semibold text-sm text-gray-800 mb-3">Today&apos;s Summary</h3>
          <div className="space-y-2">
            {[
              { label: "Check-ins today", value: allSessions.filter((s) => s.date === "2026-04-06").length, color: "text-gray-800" },
              { label: "Currently active", value: activeSessions.length, color: "text-amber-600" },
              { label: "Completed today", value: allSessions.filter((s) => s.date === "2026-04-06" && s.status === "Completed").length, color: "text-gray-800" },
              { label: "Photos uploaded", value: allSessions.filter((s) => s.date === "2026-04-06").reduce((a, s) => a + s.photos.length, 0), color: "text-gray-800" },
            ].map((item) => (
              <div key={item.label} className="flex justify-between text-sm">
                <span className="text-gray-500">{item.label}</span>
                <span className={`font-semibold ${item.color}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Last Updated */}
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl border border-gray-100">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-xs text-gray-400" /></span>
          <span className="text-xs text-gray-500">Auto-refreshes every 30 seconds</span>
        </div>
      </div>
    </div>
  );
}
